import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, AuthState, User } from '../types/auth';
import { authService } from '../services';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null
  });

  useEffect(() => {
    // Verificar autenticação inicial
    checkAuth();
    
    // Configurar listener para mudanças de autenticação
    const interval = setInterval(() => {
      checkAuth();
    }, 30000); // Verificar a cada 30 segundos

    return () => clearInterval(interval);
  }, []);

  const checkAuth = (): boolean => {
    const isAuthenticated = authService.checkAuth();
    const user = authService.getUser();
    
    setAuthState(prev => ({
      ...prev,
      isAuthenticated,
      user,
      isLoading: false
    }));

    return isAuthenticated;
  };

  const login = async (email: string, password: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await authService.login(email, password);
      checkAuth(); // Atualizar estado após login bem-sucedido
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido no login';

      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));

      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authService.logout();
    } catch (error) {
      // Continuar mesmo com erro na API
    } finally {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
    }
  };

  const clearError = (): void => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  const updateUser = (userData: Partial<User>): void => {
    authService.updateUser(userData);
    checkAuth(); // Recarregar o estado do contexto
  };

  const refreshPermissions = async (): Promise<void> => {
    try {
      await authService.refreshPermissions();
      checkAuth(); // Atualizar o contexto com as novas permissões
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    checkAuth,
    clearError,
    updateUser,
    refreshPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
